import { useState } from "react";
import type { FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AppShell, Button, Card, Reveal, TextField, tokens } from "@vh/shared-ui";
import { ApiClientError } from "../api/client";
import { useAuth } from "../auth/AuthContext";

// Sign in screen. States: idle, submitting (the button replaces its label with the indicator), error
// (the envelope message, or a plain credentials message on 401). On success the session holds the
// role and the route moves to the dashboard.
export function LoginPage() {
	const { login, status } = useAuth();
	const navigate = useNavigate();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	if (status === "authenticated") return <Navigate to="/" replace />;

	async function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setError(null);
		setSubmitting(true);
		try {
			await login(email.trim(), password);
			navigate("/", { replace: true });
		} catch (err) {
			if (err instanceof ApiClientError) {
				setError(
					err.status === 401 || err.code === "UNAUTHENTICATED"
						? "Email or password is incorrect."
						: err.message,
				);
			} else {
				setError("Something went wrong. Try again.");
			}
			setSubmitting(false);
		}
	}

	return (
		<AppShell title="Maintenance Monkey" width="form">
			<Reveal index={0}>
				<Card title="Sign in">
					<form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: tokens.space.lg }}>
						<TextField
							id="email"
							label="Email"
							type="email"
							autoComplete="username"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
							disabled={submitting}
						/>
						<TextField
							id="password"
							label="Password"
							type="password"
							autoComplete="current-password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							required
							disabled={submitting}
						/>
						{error && (
							<span role="alert" style={{ ...tokens.type.microcap, color: tokens.color.accent.red }}>
								{error}
							</span>
						)}
						<Button type="submit" variant="primary" accent="blue" loading={submitting} block>
							Sign in
						</Button>
					</form>
				</Card>
			</Reveal>
		</AppShell>
	);
}
